import { useEffect, useState } from "react";
import { buildingFrame } from "../../pixi/presentation.js";

/**
 * One building from the village atlas, drawn with CSS (no Pixi): the same
 * picture the player just tapped, shown in the building sheet.
 *
 * The atlas map is fetched once and shared. Until it arrives the box is empty
 * but keeps its size, so the sheet does not jump.
 */

interface Frame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}
interface Atlas {
  readonly frames: Record<string, { readonly frame: Frame }>;
  readonly meta: {
    readonly image?: string;
    readonly size: { readonly w: number; readonly h: number };
  };
}

const BASE = `${import.meta.env.BASE_URL}art/`;
let atlas: Promise<Atlas | null> | null = null;
let plotAtlas: Promise<Atlas | null> | null = null;
let progression: Promise<Atlas | null> | null = null;

function loadAtlas(placeholder = false): Promise<Atlas | null> {
  const load = (name: string) =>
    fetch(`${BASE}${name}.json`)
      .then((r) => (r.ok ? (r.json() as Promise<Atlas>) : null))
      .catch(() => null);
  if (placeholder) return (plotAtlas ??= load("ancient-village").then((a) => a ?? load("village")));
  atlas ??= load("ancient-buildings")
    .then((a) => a ?? load("ancient-village"))
    .then((a) => a ?? load("village"));
  return atlas;
}

export function BuildingPicture({
  id,
  size,
  level = 1,
}: {
  id: string;
  size: number;
  level?: number;
}): JSX.Element {
  const [data, setData] = useState<Atlas | null>(null);
  const [stages, setStages] = useState<Atlas | null>(null);
  useEffect(() => {
    let live = true;
    void loadAtlas(id === "plot").then((a) => {
      if (live) setData(a);
    });
    progression ??= fetch(`${BASE}ancient-progression.json`)
      .then((r) => (r.ok ? (r.json() as Promise<Atlas>) : null))
      .catch(() => null);
    void progression.then((a) => {
      if (live) setStages(a);
    });
    return () => {
      live = false;
    };
  }, [id]);

  const name = buildingFrame(id, level);
  const sheet = stages?.frames[name] ? stages : data;
  const frame = sheet?.frames[name]?.frame ?? data?.frames[id]?.frame;
  if (!sheet || !frame) return <div style={{ width: size, height: size }} aria-hidden />;

  const k = Math.min(size / frame.w, size / frame.h);
  const { w: aw, h: ah } = sheet.meta.size;
  return (
    <div
      style={{ width: size, height: size }}
      className="flex shrink-0 items-end justify-center"
      aria-hidden
    >
      <div
        data-testid="building-picture"
        style={{
          width: frame.w * k,
          height: frame.h * k,
          backgroundImage: `url(${BASE}${sheet.meta.image ?? "village.png"})`,
          backgroundSize: `${aw * k}px ${ah * k}px`,
          backgroundPosition: `${-frame.x * k}px ${-frame.y * k}px`,
          backgroundRepeat: "no-repeat",
        }}
      />
    </div>
  );
}
